import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import isFunction from "lodash/isFunction";
import isDeepEqual from "./isDeepEqual";
const id = (x) => x;
export function useStore(store, propsMapper = id) {
  const [storeState, setStoreState] = useState(() => {
    var _a;
    return (_a = store.getInitialState) == null ? void 0 : _a.call(store);
  });
  const storeStateRef = useRef(storeState);
  const mappedStoreState = useMemo(() => propsMapper(storeState), [propsMapper, storeState]);
  useEffect(() => {
    var _a;
    const unsub = store.listen((newState) => {
      if (!isDeepEqual(newState, storeStateRef.current)) {
        setStoreState(newState);
        storeStateRef.current = newState;
      }
    });
    setStoreState((_a = store.getInitialState) == null ? void 0 : _a.call(store));
    return unsub;
  }, [store]);
  return mappedStoreState;
}
function connect(Component, stores, mapProps = (props) => props) {
  const wrappedComponentName = Component.displayName || Component.name || "Unknown/Anonymous";
  const genProps = (state) => {
    const storeProps = {};
    Object.keys(stores).forEach((key) => {
      storeProps[key] = state[key];
    });
    return mapProps(storeProps);
  };
  class ConnectStoresWrapper extends React.Component {
    constructor(props) {
      super(props);
      const storeStates = Object.fromEntries(
        Object.keys(stores).map((key) => {
          const store = stores[key];
          if (store === void 0 || !isFunction(store.getInitialState)) {
            console.error(
              `Error: The store passed for the \`${key}\` property is not defined or invalid. Check the connect()-call wrapping your \`${wrappedComponentName}\` component.`
            );
            return [key, void 0];
          }
          const state = store.getInitialState();
          return [key, state];
        })
      );
      this.state = { ...this.state, ...storeStates };
    }
    componentDidMount() {
      this.unsubscribes = Object.keys(stores).map((key) => {
        const store = stores[key];
        if (store === void 0 || !isFunction(store.listen)) {
          console.error(
            `Error: The store passed for the \`${key}\` property is not defined or invalid. Check the connect()-call wrapping your \`${wrappedComponentName}\` component.`
          );
          return () => {
          };
        }
        return store.listen((partialState) => this.setState((state) => ({ ...state, [key]: partialState })));
      });
    }
    shouldComponentUpdate(nextProps, nextState) {
      const thisChildProps = genProps(this.state);
      const nextChildProps = genProps(nextState);
      return !(isDeepEqual(thisChildProps, nextChildProps) && isDeepEqual(this.props, nextProps));
    }
    componentWillUnmount() {
      this.unsubscribes.forEach((unsub) => unsub());
    }
    render() {
      const nextProps = genProps(this.state);
      const { ref, ...componentProps } = this.props;
      return /* @__PURE__ */ React.createElement(Component, { ...nextProps, ...componentProps });
    }
  }
  ConnectStoresWrapper.displayName = `ConnectStoresWrapper[${wrappedComponentName}] stores=${Object.keys(stores).join(",")}`;
  return ConnectStoresWrapper;
}
export default connect;
